import React, { useRef, useState } from 'react'
import ChipPopover from './ChipPopover'
import { componentGroups, fileTypeLabel } from './visualBuilderComponents'
import {
  COMPONENTS_BLURB,
  COMPONENTS_FILTER,
  COMPONENTS_TITLE,
  NO_COMPONENTS_MATCH,
  componentFileType,
  componentsButtonLabel
} from './layoutBuilderCopy'
import './layoutBuilder.css'

// Filter box, step-type tabs and a scrollable, one-row-open-at-a-time component list (the same row
// pattern as FlowApplications).
function ComponentsBody ({ components, applications }) {
  const [filter, setFilter] = useState('')
  const [tabKey, setTabKey] = useState(null)
  const [expandedType, setExpandedType] = useState(null)
  const groups = componentGroups(components, filter, applications)
  // The chosen tab, unless the filter emptied it and another tab still has matches. When nothing
  // matches there is no current tab (every tab is disabled and none looks active); the message
  // shows in the same fixed panel.
  const current = groups.find(group => group.key === tabKey && group.components.length > 0) ||
    groups.find(group => group.components.length > 0) ||
    null

  return (
    <>
      <p className='layoutBuilder-chip-popover-description'>{COMPONENTS_BLURB}</p>
      <input
        type='text'
        className='layoutBuilder-chip-components-filter'
        aria-label={COMPONENTS_FILTER}
        placeholder={COMPONENTS_FILTER}
        value={filter}
        onChange={event => {
          setFilter(event.target.value)
          setExpandedType(null)
          // With nothing matching, forget the chosen tab so matches returning select the first tab with matches.
          if (componentGroups(components, event.target.value, applications).every(group => group.components.length === 0)) setTabKey(null)
        }}
      />
      {groups.length > 0 && (
        <div className='layoutBuilder-chip-applications'>
          <div role='tablist' className='layoutBuilder-chip-tabs layoutBuilder-chip-tabs-grid'>
            {groups.map(group => (
              <button
                type='button'
                role='tab'
                key={group.key}
                id={`vb-components-${group.key}-tab`}
                aria-selected={group === current}
                aria-controls='vb-components-panel'
                disabled={group.components.length === 0}
                className={`layoutBuilder-chip-tab${group === current ? ' is-active' : ''}`}
                onClick={() => {
                  setTabKey(group.key)
                  setExpandedType(null)
                }}
              >{`${group.label} (${group.components.length})`}
              </button>
            ))}
          </div>
          <div role='tabpanel' id='vb-components-panel' aria-labelledby={current ? `vb-components-${current.key}-tab` : undefined} className='layoutBuilder-chip-applications-list layoutBuilder-chip-components-panel'>
            {!current && <p className='layoutBuilder-chip-applications-empty'>{NO_COMPONENTS_MATCH}</p>}
            {(current ? current.components : []).map(component => {
              const expanded = expandedType === component.component_type
              const detailId = `vb-component-${component.component_type}-detail`
              return (
                <div key={component.component_type}>
                  <button
                    type='button'
                    className='layoutBuilder-chip-application-row'
                    aria-expanded={expanded}
                    aria-controls={detailId}
                    onClick={() => setExpandedType(expanded ? null : component.component_type)}
                  >
                    <span>{component.name}</span>
                  </button>
                  <div className='layoutBuilder-chip-component-detail' id={detailId} hidden={!expanded}>
                    {component.description && <p>{component.description}</p>}
                    {component.file_type && <p>{componentFileType(fileTypeLabel(component.file_type, applications))}</p>}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}

// "Components (N)" chip on the Visual Builder card. Opens the shared chip popover (open/onToggle/
// onClose are controlled by the picker so only one popover is open at a time). Renders nothing when
// the catalog gave no components.
export default function ComponentsChip ({ components, applications, open, onToggle, onClose }) {
  const buttonRef = useRef(null)
  if (!components || components.length === 0) return null

  return (
    <>
      <button
        type='button'
        ref={buttonRef}
        className='layoutBuilder-flowfirst-chip'
        aria-expanded={open}
        aria-haspopup='dialog'
        onClick={event => {
          event.stopPropagation()
          onToggle()
        }}
      >
        <span className='layoutBuilder-flowfirst-chip-label'>{componentsButtonLabel(components.length)}</span>
      </button>
      <ChipPopover anchorRef={buttonRef} open={open} onClose={onClose} ariaLabel={COMPONENTS_TITLE} title={COMPONENTS_TITLE}>
        <ComponentsBody components={components} applications={applications} />
      </ChipPopover>
    </>
  )
}
